import React, { useCallback } from 'react';

import makeStyles from '@mui/styles/makeStyles';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Button, Divider, Fab, Paper, Typography } from '@mui/material';
import { Add, Delete, Remove } from '@mui/icons-material';
import ThemedTooltip from '@acnb/CustomComponents/Tooltip/ThemedTooltip';
import useImage from 'services/Hooks/useImage';
import { usePostChangeCartItemQuantity } from 'services/Data/CartService';
import { shouldShowQuantityPerPackageInfo } from 'services/Utilities/Utilities';

const useStyles = makeStyles(() => ({
  squareFab: {
    borderRadius: 0,
    width: 36,
    height: 36,
    minHeight: 36,
  },
}));

const CartItem = ({ item, handleOpenDeleteItemModal, handleOpenReplaceDepletedItemModal, index }) => {
  const classes = useStyles();
  const { getInventoryItemImage, getInventoryItemVariantImage } = useImage();

  const { t } = useTranslation();
  const locale = t('locale');
  const { mutate: changeQuantity } = usePostChangeCartItemQuantity();
  const bundledItems = Array.isArray(item?.bundledItems) ? item.bundledItems : [];
  const isPodLinkedBundledItem = bundledItem =>
    bundledItem?.linkedToPod === true ||
    bundledItem?.linkedToPOD === true ||
    bundledItem?.LinkedToPOD === true ||
    bundledItem?.isLinkedToPOD === true ||
    bundledItem?.IsLinkedToPOD === true;
  const safeItemQuantity = Number(item?.quantity ?? 0);
  const visibleBundledItems = bundledItems.filter(bundledItem => !isPodLinkedBundledItem(bundledItem));
  const podLinkedBundledUnitPrice = bundledItems
    .filter(isPodLinkedBundledItem)
    .reduce(
      (total, bundledItem) => total + Number(bundledItem?.unitPrice ?? 0) * Math.max(1, Number(bundledItem?.quantity ?? 1)),
      0,
    );
  const podLinkedBundledUnitPromotionPrice = bundledItems
    .filter(isPodLinkedBundledItem)
    .reduce(
      (total, bundledItem) =>
        total +
        Number(bundledItem?.unitPricePromotion ?? bundledItem?.unitPrice ?? 0) *
          Math.max(1, Number(bundledItem?.quantity ?? 1)),
      0,
    );
  const podLinkedBundledSubTotal = bundledItems
    .filter(isPodLinkedBundledItem)
    .reduce(
      (total, bundledItem) =>
        total +
        Number(
          bundledItem?.subTotal ??
            Number(bundledItem?.unitPricePromotion ?? bundledItem?.unitPrice ?? 0) *
              Math.max(1, Number(bundledItem?.quantity ?? 1)) *
              safeItemQuantity,
        ),
      0,
    );

  const variant = item?.inventoryItemVariant;
  const isVariantInactive = Boolean(variant) && variant?.isActive === false;
  const variantImageFile = variant?.files?.find(file => file.dealerPortalPrimary) ?? variant?.files?.[0];
  const variantImageSrc = variant && variantImageFile ? getInventoryItemVariantImage(variant.id, variantImageFile.id) : null;
  const handleQuantityChange = useCallback(
    quantity => {
      if (quantity <= 0) {
        handleOpenDeleteItemModal(item);
      } else if (
        quantity > item.quantity &&
        item?.inventoriedQuantityAvailable != null &&
        item.quantity >= item.inventoriedQuantityAvailable &&
        item?.inventoryItemOnceDepleted_Id
      ) {
        handleOpenReplaceDepletedItemModal?.(item);
      } else {
        changeQuantity({ ...item, quantity });
      }
    },
    [changeQuantity, handleOpenDeleteItemModal, handleOpenReplaceDepletedItemModal, item],
  );
  const promoLimitReached = item.promotionQuantity > 0 && item.quantity >= item.promotionQuantity;
  const variantPriceOverride = variant?.price != null ? Number(variant.price) : null;
  const displayUnitPrice = Math.max(0, (variantPriceOverride ?? item.unitPrice) - podLinkedBundledUnitPrice);
  const displayUnitPricePromotion = Math.max(
    0,
    (variantPriceOverride ?? item.unitPricePromotion ?? item.unitPrice) - podLinkedBundledUnitPromotionPrice,
  );
  const hasPromotion = displayUnitPricePromotion !== displayUnitPrice;
  const rawSubtotal = Number(item?.subTotal ?? displayUnitPricePromotion * safeItemQuantity);
  const displaySubtotal = Math.max(0, rawSubtotal - podLinkedBundledSubTotal);
  const imageSrc =
    variantImageSrc ||
    (item.imgSrc ? getInventoryItemImage(item?.inventoryItem_Id, item?.imgSrc.id) : '/images/brp-logo.png');
  const replacementItemLabel = item?.inventoryItemOnceDepletedName || item?.inventoryItemOnceDepletedSku;

  const depletedInventoryAlert = item?.isInventoriedItemDepleted ? (
    <Alert
      severity="warning"
      sx={{ mt: 1.5 }}
      action={
        item?.inventoryItemOnceDepleted_Id ? (
          <Button color="inherit" size="small" onClick={() => handleOpenReplaceDepletedItemModal?.(item)}>
            {t('replaceItem')}
          </Button>
        ) : null
      }>
      {replacementItemLabel
        ? t('inventoriedItemDepletedWithReplacement', {
            replacementItem: replacementItemLabel,
          })
        : t('inventoriedItemReplacementUnavailable')}
    </Alert>
  ) : null;

  const inventoriedQuantityExceededAlert = item?.isInventoriedItemQuantityExceeded ? (
    <Alert severity="error" sx={{ mt: 1.5 }}>
      {t('inventoriedItemQuantityExceededWithCounts', {
        availableQuantity: item?.inventoriedQuantityAvailable ?? 0,
        cartQuantity: item?.quantity ?? 0,
      })}
    </Alert>
  ) : null;

  const inventoriedQuantityLeftLabel =
    item?.inventoriedQuantityAvailable != null ? (
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        {t('inventoriedQuantityLeft', { count: item?.inventoriedQuantityAvailable })}
      </Typography>
    ) : null;

  const quantityControls = (
    <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ width: 130 }}>
      <Fab
        size="small"
        color="secondary"
        className={classes.squareFab}
        disabled={item.quantity <= 0}
        onClick={() => handleQuantityChange(item.quantity - 1)}>
        <Remove />
      </Fab>
      <Typography sx={{ minWidth: 24, textAlign: 'center' }}>{item.quantity}</Typography>
      <Fab
        size="small"
        color="secondary"
        className={classes.squareFab}
        sx={{ color: '#000' }}
        disabled={promoLimitReached}
        onClick={() => handleQuantityChange(item.quantity + 1)}>
        <Add />
      </Fab>
    </Box>
  );

  const variantBlock = variant && (
    <Box sx={{ mt: 1 }}>
      <Typography variant="body2" color="text.secondary">
        {t('selectedVariantLabel', { defaultValue: 'Selected variant' })}
      </Typography>
      <Typography variant="body2" fontWeight={600}>
        {(locale === 'fr'
          ? variant?.variantNameFr ?? variant?.variantNameEn ?? variant?.variantName
          : variant?.variantNameEn ?? variant?.variantName ?? variant?.variantNameFr) ||
          t('variant', { defaultValue: 'Variant' })}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {variant?.variantSku || t('skuUnavailable', { defaultValue: 'SKU unavailable' })}
      </Typography>
      {isVariantInactive && (
        <Box
          sx={{
            mt: 1,
            p: 0.75,
            borderRadius: 1,
            border: theme => `1px solid ${theme.palette.error.main}`,
            backgroundColor: theme => theme.palette.error.light,
            color: theme => theme.palette.error.contrastText,
          }}>
          <Typography variant="body2" fontWeight={600}>
            {t('variantInactiveMessage')}
          </Typography>
        </Box>
      )}
    </Box>
  );

  return (
    <Paper elevation={3} sx={{ width: '100%', borderRadius: 3, overflow: 'hidden' }}>
      {/* ── DESKTOP (md+): five-zone horizontal row ── */}
      <Box
        sx={{
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'row',
          alignItems: 'center',
          minHeight: 130,
        }}>
        {/* Zone 1: Delete button + product image */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            px: 2,
            py: 1.5,
            width: 240,
            flexShrink: 0,
          }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 44, flexShrink: 0 }}>
            <ThemedTooltip title={t('removeFromCart')} placement="top">
              <Fab color="error" size="small" className={classes.squareFab} onClick={() => handleOpenDeleteItemModal(item)}>
                <Delete />
              </Fab>
            </ThemedTooltip>
          </Box>
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', height: 160 }}>
            <Box
              component="img"
              src={imageSrc}
              alt={item?.name}
              sx={{ maxWidth: '100%', maxHeight: 150, objectFit: 'contain' }}
            />
          </Box>
        </Box>

        {/* Zone 2: Name, SKU, variant, bundled items */}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            maxWidth: { md: 360, lg: 460 },
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            px: 2.5,
            py: 1.5,
          }}>
          <Typography fontWeight={700} sx={{ fontSize: 20, lineHeight: 1.2, mb: 0.25 }}>
            {item?.name}
          </Typography>
          <Typography sx={{ fontSize: 15, color: 'text.secondary' }}>{item?.sku}</Typography>
          {variantBlock}
          {inventoriedQuantityLeftLabel}
          {inventoriedQuantityExceededAlert}
          {depletedInventoryAlert}
          {visibleBundledItems.length > 0 && (
            <>
              <Divider sx={{ my: 1 }} />
              {visibleBundledItems.map(bi => (
                <Typography key={`${bi?.sku || bi?.name}-${bi?.quantity}`} variant="body2" color="text.secondary">
                  {`${bi?.quantity}X ${bi?.name || bi?.sku}`}
                </Typography>
              ))}
            </>
          )}
        </Box>

        {/* Zone 3: Unit price */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            justifyContent: 'center',
            px: 3,
            py: 1.5,
            width: 180,
            flexShrink: 0,
            ml: 'auto',
            borderLeft: theme => `1px solid ${theme.palette.divider}`,
            alignSelf: 'stretch',
          }}>
          <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.6 }}>
            {t('unitPrice')}
          </Typography>
          {shouldShowQuantityPerPackageInfo(item.quantityPerPackage) && (
            <Typography fontSize={11} fontWeight="bold" color="text.secondary" sx={{ mb: 0.5, textAlign: 'right' }}>
              {t('quantityPerPackageInfo', { quantityPerPackage: item.quantityPerPackage })}
            </Typography>
          )}
          {hasPromotion && (
            <Typography variant="body2" color="text.secondary" sx={{ textDecoration: 'line-through' }}>
              ${displayUnitPrice.toFixed(2)}
            </Typography>
          )}
          <Typography
            fontWeight={700}
            sx={{ fontSize: 18, whiteSpace: 'nowrap' }}
            color={hasPromotion ? 'error' : 'text.primary'}>
            ${displayUnitPricePromotion.toFixed(2)} {item?.currency}
          </Typography>
        </Box>

        {/* Zone 4: Quantity controls */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: 2.5,
            py: 1.5,
            width: 175,
            flexShrink: 0,
            borderLeft: theme => `1px solid ${theme.palette.divider}`,
            alignSelf: 'stretch',
          }}>
          {quantityControls}
        </Box>

        {/* Zone 5: Subtotal */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            justifyContent: 'center',
            px: 3,
            py: 1.5,
            width: 190,
            flexShrink: 0,
            borderLeft: theme => `1px solid ${theme.palette.divider}`,
            alignSelf: 'stretch',
          }}>
          <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.6 }}>
            {t('total') || 'Total'}
          </Typography>
          {hasPromotion && (
            <Typography variant="body2" color="text.secondary" sx={{ textDecoration: 'line-through' }}>
              ${(displayUnitPrice * item.quantity).toFixed(2)}
            </Typography>
          )}
          <Typography
            fontWeight={800}
            sx={{ fontSize: 22, lineHeight: 1.1 }}
            color={hasPromotion ? 'error' : 'text.primary'}>
            ${displaySubtotal.toFixed(2)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {item.currency}
          </Typography>
        </Box>
      </Box>

      {/* ── MOBILE (xs, sm): stacked card ── */}
      <Box
        sx={{
          display: { xs: 'flex', md: 'none' },
          flexDirection: 'column',
          p: 2,
          gap: 1.5,
        }}>
        {/* Image with delete button overlaid top-right */}
        <Box sx={{ position: 'relative', display: 'flex', justifyContent: 'center', mb: 0.5 }}>
          <Box
            component="img"
            src={imageSrc}
            alt={item?.name}
            sx={{ maxWidth: '100%', maxHeight: 170, objectFit: 'contain' }}
          />
          <ThemedTooltip title={t('removeFromCart')} placement="top">
            <Fab
              color="error"
              size="small"
              className={classes.squareFab}
              sx={{ position: 'absolute', top: 0, right: 0 }}
              onClick={() => handleOpenDeleteItemModal(item)}>
              <Delete />
            </Fab>
          </ThemedTooltip>
        </Box>

        {/* Name + SKU */}
        <Box>
          <Typography fontWeight={700} sx={{ fontSize: 20, lineHeight: 1.2 }}>
            {item?.name}
          </Typography>
          <Typography sx={{ fontSize: 18, color: 'text.secondary', mt: 0.25 }}>{item?.sku}</Typography>
        </Box>

        {variantBlock}
        {inventoriedQuantityLeftLabel}
        {inventoriedQuantityExceededAlert}
        {depletedInventoryAlert}

        {visibleBundledItems.length > 0 && (
          <>
            <Divider />
            {visibleBundledItems.map(bi => (
              <Typography key={`${bi?.sku || bi?.name}-${bi?.quantity}-m`} variant="body2" color="text.secondary">
                {`${bi?.quantity}X ${bi?.name || bi?.sku}`}
              </Typography>
            ))}
          </>
        )}

        <Divider />

        {/* Unit price */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
          <Box>
            <Typography fontWeight={700} sx={{ fontSize: 15 }}>
              {t('unitPrice')}
            </Typography>
            {shouldShowQuantityPerPackageInfo(item.quantityPerPackage) && (
              <Typography fontSize={12} fontWeight="bold" color="text.secondary">
                {t('quantityPerPackageInfo', { quantityPerPackage: item.quantityPerPackage })}
              </Typography>
            )}
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            {hasPromotion && (
              <Typography variant="body2" color="text.secondary" sx={{ textDecoration: 'line-through' }}>
                ${displayUnitPrice.toFixed(2)} {item.currency}
              </Typography>
            )}
            <Typography fontWeight={700} sx={{ fontSize: 18 }} color={hasPromotion ? 'error' : 'text.primary'}>
              ${displayUnitPricePromotion.toFixed(2)} {item?.currency}
            </Typography>
          </Box>
        </Box>

        <Divider />

        {/* Quantity */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography fontWeight={700} sx={{ fontSize: 15 }}>
            {t('quantity')}
          </Typography>
          {quantityControls}
        </Box>

        <Divider />

        {/* Total */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', pb: 0.5 }}>
          <Typography fontWeight={700} sx={{ fontSize: 15 }}>
            {t('price')}
          </Typography>
          <Box sx={{ textAlign: 'right' }}>
            {hasPromotion && (
              <Typography variant="body2" color="text.secondary" sx={{ textDecoration: 'line-through' }}>
                ${(displayUnitPrice * item.quantity).toFixed(2)} {item.currency}
              </Typography>
            )}
            <Typography
              fontWeight={800}
              sx={{ fontSize: 24, lineHeight: 1.1 }}
              color={hasPromotion ? 'error' : 'text.primary'}>
              ${displaySubtotal.toFixed(2)} {item.currency}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

export default CartItem;
